import React, { Component } from 'react';
import { action, observable } from 'mobx';
import { inject, observer } from 'mobx-react';
import './style.scss';
import {
  FormCheckbox,
  FormSelect,
  FormInput,
  Button,
  Modal,
  ModalBody,
  ModalHeader
} from "shards-react";
import FeatherIcon from 'feather-icons-react';
import PageTitle from "../../../components/common/PageTitle";
import { toast } from "react-toastify";
import { convertVideoToAnimatedWebp, uploadFileToCafe24 } from "../../../utils/mediaConvert";

const MAX_PRODUCTS = 5;

// 디자인(방식) — 새 디자인은 여기에 항목만 추가하면 관리자에서 선택 가능 (임베드 렌더러에 동일 key 구현)
const DESIGN_TYPES = [
  { key: 'grid', label: '그리드(바둑판)' },
];

const REVIEW_SORTS = [
  { key: 'newst', label: '최신순' },
  { key: 'grade', label: '평점순' },
];

const COLS = [{ key: 2, label: '2열' }, { key: 3, label: '3열' }];

const ALIGNS = [
  { key: 'center', label: '가운데' },
  { key: 'left', label: '왼쪽' },
  { key: 'right', label: '오른쪽' },
];

const DISPLAY_AREAS = [
  { key: 'main', label: '메인(인덱스) 자동 노출 + 코드' },
  { key: 'code', label: '코드로만 사용' },
];

// 부모 요소 기준 삽입 위치 (기존 위젯과 동일)
const INSERT_POSITIONS = [
  { key: 'append', label: '지정 요소 내부 맨 끝' },
  { key: 'prepend', label: '지정 요소 내부 맨 앞' },
  { key: 'after', label: '지정 요소 바로 뒤(형제)' },
  { key: 'before', label: '지정 요소 바로 앞(형제)' },
  { key: 'html', label: '지정 요소 내용 대체' },
];

@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))
@inject(({ widget }) => ({ getWidgetList: widget.getWidgetList, updateWidget: widget.updateWidget, removeWidget: widget.removeWidget }))
@inject(({ product }) => ({ getProductList: product.getProductList }))
@inject(({ common }) => ({ cafe24UploadFiles: common.cafe24UploadFiles }))
@observer
class ShortFormWidget extends Component {

  @observable store = {};
  @observable teamUser = {};
  @observable isLogin = false;
  @observable isUse = false;
  @observable loaded = false;
  @observable saving = false;

  // 폼 상태 (Widget.display 에 저장되는 설정 + 위젯 메타)
  @observable form = {
    _id: null,
    title: '숏폼위젯',
    isTitleDis: true,
    isDisplay: true,
    displayArea: 'main',
    designType: 'grid',
    reviewSort: 'newst',
    col: 2,
    align: 'center',   // 정렬 (기본 가운데) — left | center | right
    maxWidth: '',      // 최대 너비 (예: 1200px, 100%) — 빈 값이면 제한 없음
    // 노출 위치 — 부모 요소 id/class 로 지정 (기존 위젯과 동일 방식, PC/모바일 분리)
    parentSelector: '',
    parentSelectorMobile: '',
    insertPosition: 'append',
    insertPositionMobile: '',   // 빈 값 = PC와 동일
    products: []   // [{ platformProductId, productName, image, webpUrl, uploading }]
  };

  // 상품 검색 모달
  @observable modals = { isProductSearch: false };
  @observable productSearch = { keyword: '', results: [], isSearching: false, searched: false };

  constructor(props) {
    super(props);
    this.init();
  }

  @action
  init = async () => {
    if (!this.props.isLogin) return;
    this.isLogin = this.props.isLogin;
    this.teamUser = this.props.me;
    this.store = this.props.me.store ? this.props.me.store : {};
    this.isUse = !!this.teamUser.isPaidShop;

    // 기존 숏폼위젯 1개 로드 (관리자는 1개만 운용)
    const baseP = {
      store_id: this.teamUser.selStore_id,
      shopNo: this.teamUser.selShopNo ? this.teamUser.selShopNo : 1,
      limit: 50,
      page: 1
    };
    // 우선 type 필터로 조회(백엔드 배포 시). 미지원(미배포)이면 전체 조회 후 클라이언트 필터로 폴백.
    let res = await this.props.getWidgetList(Object.assign({}, baseP, { type: 'shortform' }));
    let docs = (res && res.docs) ? res.docs : null;
    if (docs === null) {
      res = await this.props.getWidgetList(baseP);
      docs = (res && res.docs) ? res.docs.filter(w => w.type === 'shortform') : [];
    }
    if (docs.length > 0) {
      this._loadFromWidget(docs[0]);
    }
    this.loaded = true;
  };

  @action
  _loadFromWidget = (w) => {
    const d = w.display || {};
    this.form = {
      _id: w._id,
      title: w.title || '숏폼위젯',
      isTitleDis: w.isTitleDis === true || w.isTitleDis === 'true',
      isDisplay: w.isDisplay === true || w.isDisplay === 'true',
      displayArea: w.displayArea || 'main',
      designType: d.designType || 'grid',
      reviewSort: d.reviewSort || 'newst',
      col: d.col || 2,
      align: d.align || 'center',
      maxWidth: d.maxWidth || '',
      parentSelector: d.parentSelector || '',
      parentSelectorMobile: d.parentSelectorMobile || '',
      insertPosition: d.insertPosition || 'append',
      insertPositionMobile: d.insertPositionMobile || '',
      products: (Array.isArray(d.products) ? d.products : []).map(p => ({
        platformProductId: String(p.platformProductId),
        productName: p.productName || '',
        image: p.image || '',
        price: p.price || 0,
        discountPrice: p.discountPrice || 0,
        webpUrl: p.webpUrl || '',
        uploading: false
      }))
    };
  };

  // ===== 설정 변경 =====
  @action onChangeField = (name, value) => { this.form[name] = value; };
  @action toggleField = (name) => { this.form[name] = !this.form[name]; };

  // ===== 상품 검색/선택 =====
  @action openProductSearch = () => {
    if (this.form.products.length >= MAX_PRODUCTS) {
      toast('상품은 최대 ' + MAX_PRODUCTS + '개까지 선택할 수 있습니다.');
      return;
    }
    this.productSearch = { keyword: '', results: [], isSearching: false, searched: false };
    this.modals.isProductSearch = true;
  };

  @action searchProducts = async () => {
    const keyword = (this.productSearch.keyword || '').trim();
    this.productSearch.isSearching = true;
    const res = await this.props.getProductList({
      store_id: this.teamUser.selStore_id,
      shopNo: this.teamUser.selShopNo ? this.teamUser.selShopNo : 1,
      limit: 20,
      page: 1,
      searchParams: keyword ? { keyword: keyword } : {}
    });
    this._setSearchResults(res && res.docs ? res.docs : []);
  };

  @action _setSearchResults = (docs) => {
    this.productSearch.isSearching = false;
    this.productSearch.searched = true;
    this.productSearch.results = docs;
  };

  @action addProduct = (p) => {
    if (this.form.products.length >= MAX_PRODUCTS) {
      toast('상품은 최대 ' + MAX_PRODUCTS + '개까지 선택할 수 있습니다.');
      return;
    }
    const pid = String(p.platformProductId);
    if (this.form.products.some(x => String(x.platformProductId) === pid)) {
      toast('이미 추가된 상품입니다.');
      return;
    }
    const image = p.image || (p.images && p.images.s_1) || '';
    this.form.products = this.form.products.concat([{
      platformProductId: pid,
      productName: p.productName || '',
      image: image,
      price: p.price || 0,                  // 정가(소비자가)
      discountPrice: p.discountPrice || 0,  // 판매가(최종)
      webpUrl: '',
      uploading: false
    }]);
    if (this.form.products.length >= MAX_PRODUCTS) {
      this.modals.isProductSearch = false;
    }
  };

  @action removeProduct = (idx) => {
    this.form.products = this.form.products.filter((_, i) => i !== idx);
  };

  // ===== 상품별 영상 → 움직이는 WebP → 카페24 업로드 =====
  onSelectVideo = async (idx, event) => {
    const file = event.target.files && event.target.files[0];
    if (event.target) { try { event.target.value = ''; } catch (e) {} }
    if (!file) return;
    if (!/^video\//.test(file.type || '')) { toast.error('영상 파일을 선택해 주세요.'); return; }
    if (file.size / 1000000 > 50) { toast.error('영상은 50MB 이하만 업로드할 수 있습니다.'); return; }

    this._setProduct(idx, { uploading: true });
    try {
      const outName = 'sfm_' + (this.form.products[idx].platformProductId || 'p') + '_' + Date.now();
      const webpFile = await convertVideoToAnimatedWebp(file, outName);
      const uploaded = await uploadFileToCafe24(webpFile, {
        storeId: this.teamUser.selStore_id,
        cafe24UploadFiles: this.props.cafe24UploadFiles
      });
      this._setProduct(idx, { webpUrl: uploaded.url, uploading: false });
      toast.success('움직이는 이미지가 업로드되었습니다.');
    } catch (e) {
      this._setProduct(idx, { uploading: false });
      toast.error('영상 변환/업로드 실패: ' + (e.message || '알 수 없는 오류'));
    }
  };

  @action _setProduct = (idx, patch) => {
    this.form.products = this.form.products.map((p, i) => i === idx ? Object.assign({}, p, patch) : p);
  };

  _comma = (n) => String(parseInt(n, 10) || 0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // ===== 저장 =====
  saveWidget = async () => {
    if (!this.isUse) {
      toast.error('유료 플랜 전용 기능입니다. 결제 페이지에서 플랜을 활성화해주세요.');
      return;
    }
    if (this.form.products.length === 0) {
      toast.error('상품을 1개 이상 선택해 주세요.');
      return;
    }
    const noWebp = this.form.products.filter(p => !p.webpUrl);
    if (noWebp.length > 0) {
      if (!window.confirm('움직이는 이미지가 없는 상품이 ' + noWebp.length + '개 있습니다. 그래도 저장할까요? (이미지 없는 상품은 상품 이미지로 표시됩니다)')) {
        return;
      }
    }

    this.saving = true;
    const _this = this;
    const products = this.form.products.map((p, i) => ({
      platformProductId: p.platformProductId,
      productName: p.productName,
      image: p.image,
      price: p.price || 0,
      discountPrice: p.discountPrice || 0,
      webpUrl: p.webpUrl || '',
      order: i
    }));

    const data = {
      store_id: this.teamUser.selStore_id,
      shopNo: this.teamUser.selShopNo ? this.teamUser.selShopNo : 1,
      type: 'shortform',
      title: this.form.title,
      isTitleDis: !!this.form.isTitleDis,
      displayArea: this.form.displayArea,
      isDisplay: this.form.isDisplay ? 'true' : 'false',  // schema: isDisplay = String
      display: {
        designType: this.form.designType,
        reviewSort: this.form.reviewSort,
        reviewLimit: 5,
        col: this.form.col,
        align: this.form.align || 'center',
        maxWidth: (this.form.maxWidth || '').trim(),
        parentSelector: (this.form.parentSelector || '').trim(),
        parentSelectorMobile: (this.form.parentSelectorMobile || '').trim(),
        insertPosition: this.form.insertPosition || 'append',
        insertPositionMobile: this.form.insertPositionMobile || '',
        products: products
      }
    };
    if (this.form._id) data.id = this.form._id;

    this.props.updateWidget(data, action(function (res) {
      _this.saving = false;
      if (res && res.errors && res.errors.length > 0) {
        toast.error('저장 실패: ' + res.errors[0].message);
        return;
      }
      toast.success('숏폼위젯이 저장되었습니다.');
      // 신규 생성 시 _id 확보를 위해 재조회
      if (!_this.form._id) {
        _this.init();
      }
    }));
  };

  getCodeSnippet = () => {
    const id = this.form._id || '{저장 후 생성됨}';
    return '<div class="onreple-shortform" data-widget-id="' + id + '"></div>';
  };

  copyCode = () => {
    if (!this.form._id) { toast('먼저 저장해 주세요.'); return; }
    try {
      navigator.clipboard.writeText(this.getCodeSnippet());
      toast.success('코드가 복사되었습니다.');
    } catch (e) {
      toast('코드를 직접 복사해 주세요.');
    }
  };

  render() {
    const f = this.form;
    const productImg = (p) => p.image || '';

    return (
      <div className="main-content sfm-page">
        <PageTitle />

        <div className="sfm-guide">
          <FeatherIcon icon="info" size="14" aria-hidden="true" />
          <span>
            상품을 최대 <strong>{MAX_PRODUCTS}개</strong> 선택하고 각 상품의 <strong>영상을 업로드</strong>하면 움직이는 이미지(WebP)로 변환되어 카페24에 저장됩니다.
            쇼핑몰에는 <strong>상품정보 + 움직이는 이미지 + 리뷰 {5}개</strong>가 표시됩니다.
          </span>
        </div>

        {(this.isLogin && !this.isUse) && (
          <div className="sfm-paid-notice">
            <FeatherIcon icon="lock" size="13" aria-hidden="true" /> 저장은 유료 플랜 전용입니다. 화면 구성은 자유롭게 확인할 수 있습니다.
          </div>
        )}

        {/* 설정 */}
        <section className="sfm-card">
          <div className="sfm-card-head">
            <FeatherIcon icon="sliders" size="16" aria-hidden="true" />
            <h3>위젯 설정</h3>
          </div>
          <div className="sfm-settings-grid">
            <div className="sfm-field">
              <label>위젯 제목</label>
              <FormInput value={f.title} onChange={e => this.onChangeField('title', e.target.value)} />
            </div>
            <div className="sfm-field">
              <label>디자인(방식)</label>
              <FormSelect value={f.designType} onChange={e => this.onChangeField('designType', e.target.value)}>
                {DESIGN_TYPES.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </FormSelect>
            </div>
            <div className="sfm-field">
              <label>리뷰 정렬</label>
              <FormSelect value={f.reviewSort} onChange={e => this.onChangeField('reviewSort', e.target.value)}>
                {REVIEW_SORTS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </FormSelect>
            </div>
            <div className="sfm-field">
              <label>열 수</label>
              <FormSelect value={f.col} onChange={e => this.onChangeField('col', parseInt(e.target.value, 10))}>
                {COLS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </FormSelect>
            </div>
            <div className="sfm-field">
              <label>정렬</label>
              <FormSelect value={f.align} onChange={e => this.onChangeField('align', e.target.value)}>
                {ALIGNS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </FormSelect>
            </div>
            <div className="sfm-field">
              <label>최대 너비</label>
              <FormInput value={f.maxWidth} placeholder="예: 1200px (비우면 제한 없음)"
                         onChange={e => this.onChangeField('maxWidth', e.target.value)} />
            </div>
            <div className="sfm-field">
              <label>노출 위치</label>
              <FormSelect value={f.displayArea} onChange={e => this.onChangeField('displayArea', e.target.value)}>
                {DISPLAY_AREAS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </FormSelect>
            </div>
            <div className="sfm-field sfm-field--toggle">
              <label>노출 사용</label>
              <FormCheckbox toggle small checked={!!f.isDisplay} onChange={() => this.toggleField('isDisplay')}>
                {f.isDisplay ? '노출' : '숨김'}
              </FormCheckbox>
            </div>
          </div>
        </section>

        {/* 노출 위치 — 부모 요소 id/class 지정 (기존 위젯과 동일 방식, PC/모바일 분리) */}
        <section className="sfm-card">
          <div className="sfm-card-head">
            <FeatherIcon icon="crosshair" size="16" aria-hidden="true" />
            <h3>노출 위치 (부모 요소 지정)</h3>
          </div>
          <p className="sfm-code-desc">위젯을 넣을 부모 요소의 <strong>id 또는 class</strong>를 입력하면 그 위치에 정확히 표시됩니다. 비워두면 위의 "노출 위치"/코드 배치를 따릅니다. (모바일 비우면 PC 설정 사용)</p>
          <div className="sfm-settings-grid">
            <div className="sfm-field">
              <label>부모 클래스/ID (PC)</label>
              <FormInput value={f.parentSelector} placeholder=".class-name 또는 #element-id"
                         onChange={e => this.onChangeField('parentSelector', e.target.value)} />
            </div>
            <div className="sfm-field">
              <label>부모 클래스/ID (모바일)</label>
              <FormInput value={f.parentSelectorMobile} placeholder="비우면 PC 설정 사용"
                         onChange={e => this.onChangeField('parentSelectorMobile', e.target.value)} />
            </div>
            <div className="sfm-field">
              <label>삽입 위치 (PC)</label>
              <FormSelect value={f.insertPosition} onChange={e => this.onChangeField('insertPosition', e.target.value)}>
                {INSERT_POSITIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </FormSelect>
            </div>
            <div className="sfm-field">
              <label>삽입 위치 (모바일)</label>
              <FormSelect value={f.insertPositionMobile} onChange={e => this.onChangeField('insertPositionMobile', e.target.value)}>
                <option value="">PC와 동일</option>
                {INSERT_POSITIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
              </FormSelect>
            </div>
          </div>
        </section>

        {/* 상품 + 영상 */}
        <section className="sfm-card">
          <div className="sfm-card-head">
            <FeatherIcon icon="film" size="16" aria-hidden="true" />
            <h3>상품 & 영상 ({f.products.length}/{MAX_PRODUCTS})</h3>
            <Button size="sm" theme="primary" className="sfm-add-btn"
                    disabled={f.products.length >= MAX_PRODUCTS}
                    onClick={() => this.openProductSearch()}>
              <FeatherIcon icon="plus" size="13" aria-hidden="true" /> 상품 추가
            </Button>
          </div>

          {f.products.length === 0 ? (
            <div className="sfm-empty">
              <FeatherIcon icon="inbox" size="34" aria-hidden="true" />
              <p>상품을 추가해 숏폼을 구성해 보세요.</p>
            </div>
          ) : (
            <div className="sfm-product-list">
              {f.products.map((p, idx) => (
                <div className="sfm-product-row" key={p.platformProductId}>
                  <div className="sfm-prod-thumb">
                    {productImg(p) ? <img src={productImg(p)} alt="" /> : <span className="sfm-prod-thumb-empty"><FeatherIcon icon="image" size="16" /></span>}
                  </div>
                  <div className="sfm-prod-meta">
                    <div className="sfm-prod-name">{p.productName || '(상품명 없음)'}</div>
                    <div className="sfm-prod-id">상품번호 {p.platformProductId}</div>
                    {(p.discountPrice || p.price) ? (
                      <div className="sfm-prod-price">
                        {(p.price && p.price > (p.discountPrice || p.price))
                          ? <del>{this._comma(p.price)}원</del> : null}
                        <strong>{this._comma(p.discountPrice || p.price)}원</strong>
                      </div>
                    ) : null}
                  </div>
                  <div className="sfm-prod-webp">
                    {p.uploading ? (
                      <span className="sfm-uploading"><FeatherIcon icon="loader" size="13" /> 변환·업로드 중…</span>
                    ) : p.webpUrl ? (
                      <div className="sfm-webp-preview">
                        <img src={p.webpUrl} alt="animated" />
                        <span className="sfm-webp-ok"><FeatherIcon icon="check-circle" size="12" /> 적용됨</span>
                      </div>
                    ) : (
                      <span className="sfm-webp-none">영상 미등록</span>
                    )}
                    <label className="sfm-upload-btn">
                      <FeatherIcon icon="upload" size="12" aria-hidden="true" /> {p.webpUrl ? '영상 교체' : '영상 업로드'}
                      <input type="file" accept="video/*" style={{ display: 'none' }}
                             disabled={p.uploading}
                             onChange={e => this.onSelectVideo(idx, e)} />
                    </label>
                  </div>
                  <button type="button" className="sfm-del-btn" onClick={() => this.removeProduct(idx)} aria-label="상품 제거">
                    <FeatherIcon icon="trash-2" size="14" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 코드 스니펫 */}
        <section className="sfm-card">
          <div className="sfm-card-head">
            <FeatherIcon icon="code" size="16" aria-hidden="true" />
            <h3>코드로 배치</h3>
          </div>
          <p className="sfm-code-desc">원하는 위치에 아래 코드를 넣으면 그 자리에 숏폼위젯이 표시됩니다. (노출 위치를 "메인 자동 노출"로 두면 메인 페이지에도 자동 표시)</p>
          <div className="sfm-code-box">
            <code>{this.getCodeSnippet()}</code>
            <Button size="sm" theme="light" onClick={() => this.copyCode()}>
              <FeatherIcon icon="copy" size="12" aria-hidden="true" /> 복사
            </Button>
          </div>
        </section>

        {/* 저장 바 */}
        <div className="sfm-save-bar">
          <Button theme="primary" disabled={this.saving} onClick={() => this.saveWidget()}>
            <FeatherIcon icon={this.saving ? 'loader' : 'save'} size="14" aria-hidden="true" />
            {this.saving ? ' 저장 중…' : (this.form._id ? ' 변경사항 저장' : ' 숏폼위젯 생성')}
          </Button>
        </div>

        {/* 상품 검색 모달 */}
        <Modal size="lg" className="modal-text sfm-search-modal" open={this.modals.isProductSearch}
               toggle={() => this.modals.isProductSearch = false} centered={true}>
          <ModalHeader toggle={() => this.modals.isProductSearch = false}>상품 추가</ModalHeader>
          <ModalBody>
            <div className="sfm-search-bar">
              <FormInput placeholder="상품명 검색" value={this.productSearch.keyword}
                         onChange={e => this.productSearch.keyword = e.target.value}
                         onKeyPress={e => { if (e.key === 'Enter') this.searchProducts(); }} />
              <Button theme="primary" onClick={() => this.searchProducts()}>
                <FeatherIcon icon="search" size="13" aria-hidden="true" /> 검색
              </Button>
            </div>
            <div className="sfm-search-results">
              {this.productSearch.isSearching ? (
                <div className="sfm-search-state"><FeatherIcon icon="loader" size="14" /> 검색 중…</div>
              ) : this.productSearch.results.length > 0 ? (
                this.productSearch.results.map(p => {
                  const added = this.form.products.some(x => String(x.platformProductId) === String(p.platformProductId));
                  const img = p.image || (p.images && p.images.s_1) || '';
                  return (
                    <div className="sfm-search-item" key={p._id || p.platformProductId}>
                      <div className="sfm-prod-thumb">
                        {img ? <img src={img} alt="" /> : <span className="sfm-prod-thumb-empty"><FeatherIcon icon="image" size="14" /></span>}
                      </div>
                      <div className="sfm-prod-meta">
                        <div className="sfm-prod-name">{p.productName}</div>
                        <div className="sfm-prod-id">상품번호 {p.platformProductId}</div>
                      </div>
                      <Button size="sm" theme={added ? 'light' : 'primary'} disabled={added}
                              onClick={() => this.addProduct(p)}>
                        {added ? '추가됨' : '추가'}
                      </Button>
                    </div>
                  );
                })
              ) : this.productSearch.searched ? (
                <div className="sfm-search-state">검색 결과가 없습니다.</div>
              ) : (
                <div className="sfm-search-state">상품명을 검색해 추가하세요.</div>
              )}
            </div>
          </ModalBody>
        </Modal>
      </div>
    );
  }
}

export default ShortFormWidget;
